/**
 * VariableVersionService - Manual versioning for project-level variable sets
 *
 * Stores immutable snapshots of variable configurations in Firestore subcollection.
 * Versioning is MANUAL — user clicks "Save Version", not auto on every edit.
 * Tasks are NOT versioned — experiments with tasks = different strategies.
 *
 * Storage:
 *   projects/{projectId}/variableSetVersions/{version} ← immutable snapshots
 *   projects/{projectId} → variableSet.currentVersion  ← pointer to latest saved version
 *
 * Usage:
 * ```typescript
 * const service = new VariableVersionService();
 *
 * // Save current state as version
 * const v1 = await service.createVersion(projectId, variables, 'Initial setup', userId);
 *
 * // List all versions
 * const versions = await service.listVersions(projectId);
 *
 * // Restore old version (creates NEW version with old content)
 * const v3 = await service.restoreVersion(projectId, 1, userId);
 *
 * // Promote strategy variables to project
 * const v4 = await service.promoteFromStrategy(projectId, strategyId, strategyVars, userId, 'Successful experiment');
 * ```
 */

import {
  getFirestore,
  collection,
  doc,
  getDoc,
  getDocs,
  runTransaction,
  type Firestore,
  query,
  orderBy,
  limit,
  Timestamp,
} from 'firebase/firestore';

import { cloneSnapshot, sameSnapshot } from '@modules/firebase-core/utils/variableSnapshot';

// ═══════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════

export interface VariableSetVersion {
  version: number;
  variables: Record<string, any>; // FunnelVariable definitions
  createdAt: string;              // ISO timestamp
  createdBy?: string;             // userId
  changeDescription?: string;     // human-readable description
  parentVersion?: number;         // which version this was branched from
  sourceType: 'project' | 'strategy';
  sourceId: string;               // projectId or strategyId
}

export interface VariableSetDiff {
  added: string[];                // variable IDs added
  removed: string[];              // variable IDs removed
  modified: Array<{
    id: string;
    changes: string[];            // human-readable change descriptions
  }>;
}

// ═══════════════════════════════════════════════════════
// SERVICE
// ═══════════════════════════════════════════════════════

export class VariableVersionService {
  constructor(private readonly db: Firestore = getFirestore(), private readonly transact: typeof runTransaction = runTransaction) {}

  private getVersionsCollectionPath(projectId: string): string {
    return `projects/${projectId}/variableSetVersions`;
  }

  /**
   * Save current variable state as a new version (manual action).
   * Append-only: never overwrites existing versions.
   */
  async createVersion(
    projectId: string,
    variables: Record<string, any>,
    changeDescription: string,
    userId?: string,
    parentVersion?: number,
    sourceType: 'project' | 'strategy' = 'project',
    sourceId?: string,
  ): Promise<VariableSetVersion> {
    return this.writeVersion(projectId, { variables, changeDescription, userId, parentVersion, sourceType, sourceId });
  }

  private async writeVersion(projectId: string, input: {
    variables?: Record<string, any>; changeDescription: string; userId?: string;
    parentVersion?: number; sourceType?: 'project' | 'strategy'; sourceId?: string;
    restoreFrom?: number; applyToProject?: boolean;
  }): Promise<VariableSetVersion> {
    // Existing historical snapshots may be ahead of the project pointer. This
    // query is only a floor; allocation and absence checks occur in the transaction.
    const observedNext = await this.getNextVersion(projectId);
    const supplied = input.variables === undefined ? undefined : cloneSnapshot(input.variables);
    return this.transact(this.db, async transaction => {
      const projectRef = doc(this.db, 'projects', projectId);
      const project = await transaction.get(projectRef);
      if (!project.exists()) throw new Error(`Project ${projectId} not found`);
      const current = project.data().variableSet?.currentVersion ?? 0;
      if (!Number.isSafeInteger(current) || current < 0) throw new Error('Invalid current variable version; repair the pointer before saving.');
      if (current > 0 && !(await transaction.get(doc(this.db, this.getVersionsCollectionPath(projectId), String(current)))).exists()) {
        throw new Error('Current variable version is missing; repair the pointer before saving.');
      }
      let variables = supplied;
      let parentVersion = input.parentVersion ?? (current || undefined);
      if (input.restoreFrom !== undefined) {
        if (!Number.isSafeInteger(input.restoreFrom) || input.restoreFrom < 1) throw new Error('Restore version must be a positive integer.');
        const source = await transaction.get(doc(this.db, this.getVersionsCollectionPath(projectId), String(input.restoreFrom)));
        if (!source.exists()) throw new Error(`Version ${input.restoreFrom} not found`);
        variables = cloneSnapshot(source.data().variables); parentVersion = input.restoreFrom;
      } else if (input.sourceType === 'strategy') {
        if (!input.sourceId) throw new Error('Strategy source id is required.');
        const source = await transaction.get(doc(this.db, 'projects', projectId, 'strategies', input.sourceId));
        if (!source.exists() || !sameSnapshot(source.data().variables ?? {}, supplied)) throw new Error('Strategy variables changed; reload before promoting.');
      } else if (!sameSnapshot(project.data().variableSet?.variables ?? {}, supplied)) {
        throw new Error('Project variables changed; reload before saving a version.');
      }
      if (!variables || typeof variables !== 'object' || Array.isArray(variables)) throw new Error('Variable snapshot must be a map.');
      if (parentVersion !== undefined && (!Number.isSafeInteger(parentVersion) || parentVersion < 0)) throw new Error('Invalid parent variable version.');
      if (parentVersion === 0) parentVersion = undefined; // Historical bootstrap sentinel.
      if (parentVersion !== undefined && parentVersion !== current && parentVersion !== input.restoreFrom
        && !(await transaction.get(doc(this.db, this.getVersionsCollectionPath(projectId), String(parentVersion)))).exists()) {
        throw new Error('Parent variable version is missing.');
      }
      let nextVersion = Math.max(observedNext, current + 1);
      if (!Number.isSafeInteger(nextVersion)) throw new Error('Variable version range exhausted.');
      let versionRef = doc(this.db, this.getVersionsCollectionPath(projectId), String(nextVersion));
      // Occupied legacy/orphan slots are never overwritten, even if the outside
      // query became stale. The bounded scan fails closed under excessive drift.
      for (let collision = 0; (await transaction.get(versionRef)).exists(); collision++) {
        if (collision >= 9 || !Number.isSafeInteger(nextVersion + 1)) throw new Error('Variable version allocation changed; reload and retry.');
        versionRef = doc(this.db, this.getVersionsCollectionPath(projectId), String(++nextVersion));
      }
      const createdAt = Timestamp.now();
      const version: VariableSetVersion = { version: nextVersion, variables, createdAt: createdAt.toDate().toISOString(),
        changeDescription: input.changeDescription, sourceType: input.sourceType ?? 'project', sourceId: input.sourceId ?? projectId,
        ...(input.userId !== undefined ? { createdBy: input.userId } : {}),
        ...(parentVersion !== undefined ? { parentVersion } : {}) };
      transaction.set(versionRef, { ...version, createdAt });
      transaction.set(projectRef, { variableSet: { currentVersion: nextVersion,
        ...(input.applyToProject ? { variables } : {}) } },
      { mergeFields: ['variableSet.currentVersion', ...(input.applyToProject ? ['variableSet.variables'] : [])] });
      return version;
    });
  }

  /**
   * List all versions (newest first).
   */
  async listVersions(projectId: string): Promise<VariableSetVersion[]> {
    const versionsRef = collection(
      this.db,
      this.getVersionsCollectionPath(projectId),
    );
    const q = query(versionsRef, orderBy('version', 'desc'));
    const snapshot = await getDocs(q);

    return snapshot.docs.map((doc) => {
      const data = doc.data();
      return {
        ...data,
        version: Number(doc.id),
        createdAt: data.createdAt?.toDate?.()?.toISOString?.() ?? data.createdAt,
      } as VariableSetVersion;
    });
  }

  /**
   * Get a specific version.
   */
  async getVersion(projectId: string, version: number): Promise<VariableSetVersion | null> {
    const versionRef = doc(
      this.db,
      this.getVersionsCollectionPath(projectId),
      String(version),
    );
    const snapshot = await getDoc(versionRef);
    if (!snapshot.exists()) return null;

    const data = snapshot.data();
    return {
      ...data,
      version,
      createdAt: data.createdAt?.toDate?.()?.toISOString?.() ?? data.createdAt,
    } as VariableSetVersion;
  }

  /**
   * Restore a previous version (creates NEW version with old content).
   * Append-only: the old version is preserved, a new one is created.
   */
  async restoreVersion(
    projectId: string,
    versionToRestore: number,
    userId?: string,
  ): Promise<VariableSetVersion> {
    return this.writeVersion(projectId, { restoreFrom: versionToRestore, applyToProject: true,
      changeDescription: `Restored from version ${versionToRestore}`, userId });
  }

  /**
   * Promote strategy variables to project (creates new project version).
   */
  async promoteFromStrategy(
    projectId: string,
    strategyId: string,
    strategyVariables: Record<string, any>,
    userId?: string,
    changeDescription?: string,
  ): Promise<VariableSetVersion> {
    return this.writeVersion(projectId, { variables: strategyVariables, applyToProject: true,
      changeDescription: changeDescription ?? 'Promoted from strategy', userId, sourceType: 'strategy', sourceId: strategyId });
  }

  /**
   * Diff two versions — shows what changed.
   */
  diffVersions(v1: VariableSetVersion, v2: VariableSetVersion): VariableSetDiff {
    const keys1 = new Set(Object.keys(v1.variables));
    const keys2 = new Set(Object.keys(v2.variables));

    const added: string[] = [];
    const removed: string[] = [];
    const modified: Array<{ id: string; changes: string[] }> = [];

    // Added in v2
    for (const key of keys2) {
      if (!keys1.has(key)) added.push(key);
    }

    // Removed in v2
    for (const key of keys1) {
      if (!keys2.has(key)) removed.push(key);
    }

    // Modified
    for (const key of keys1) {
      if (!keys2.has(key)) continue;
      const var1 = v1.variables[key];
      const var2 = v2.variables[key];
      const changes: string[] = [];

      if (var1.name !== var2.name) changes.push(`name: "${var1.name}" → "${var2.name}"`);
      if (var1.type !== var2.type) changes.push(`type: ${var1.type} → ${var2.type}`);
      if (var1.required !== var2.required) changes.push(`required: ${var1.required} → ${var2.required}`);
      if (var1.description !== var2.description) changes.push('description changed');
      if (JSON.stringify(var1.options) !== JSON.stringify(var2.options)) changes.push('options changed');
      if (JSON.stringify(var1.taskIds) !== JSON.stringify(var2.taskIds)) changes.push('task scope changed');
      for (const field of ['collectionContract', 'insightSource', 'excludeFromAnalytics', 'scope', 'boundScreenIds', 'screenBindings', 'boundSectionId', 'min', 'max', 'integer', 'minDate', 'maxDate', 'minSelections', 'maxSelections']) {
        if (!sameSnapshot(var1[field], var2[field])) changes.push(`${field} changed`);
      }

      if (changes.length > 0) {
        modified.push({ id: key, changes });
      }
    }

    return { added, removed, modified };
  }

  // ── Private helpers ──

  private async getNextVersion(projectId: string): Promise<number> {
    const versionsRef = collection(
      this.db,
      this.getVersionsCollectionPath(projectId),
    );
    const q = query(versionsRef, orderBy('version', 'desc'), limit(1));
    const snapshot = await getDocs(q);

    if (snapshot.empty) return 1;
    const highest = Number(snapshot.docs[0].id);
    if (!Number.isSafeInteger(highest) || highest < 1 || !Number.isSafeInteger(highest + 1)) throw new Error('Invalid historical variable version.');
    return highest + 1;
  }

}

/** Singleton instance */
export const variableVersionService = new VariableVersionService();
